import React, { createContext, useEffect, useMemo, useRef, useState } from "react"
import HeaderDescription from "@/components/common/Header&Description"
import GridContainer from "@/components/common/GridContainer"
import { getCssSizeValue, getPaddingsClassFromData } from "@/utils/utils"
import DesktopNavigator from "@/components/BranchNavigator/Desktop/Navigator"
import MobileNavigator from "@/components/BranchNavigator/Mobile/Navigator"
import { useIsMobile } from "@/hooks/useDevice"
import useUnits from "@/components/BranchNavigator/Units/useUnits"
import useCurrentBranchData from "@/hooks/useCurrentBranchData"
import { NAVIGATOR_VIEW_TYPE } from "@/constants"
import useCustomContext from "@/hooks/useCustomContext"
import styled from "styled-components"
import { BREAKPOINTS } from "@/styles/themeSettings"

const Wrapper = styled(GridContainer)`
	row-gap: ${getCssSizeValue(36, BREAKPOINTS.MOBILE)};
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		row-gap: var(--grid-gap);
	}
`

const INIT_CONTEXT = {
	currentUnit: {},
	setCurrentUnit: (() => {}) | {},
	view: NAVIGATOR_VIEW_TYPE.MAP,
	setView: (() => {}) | {},
	isLoading: false,
	container: () => ({ current: undefined }),
	units: [],
	isTextileCollection: false,
}

const NavigatorContext = createContext({ ...INIT_CONTEXT })

const BranchNavigator = ({ data }) => {
	const paddings = getPaddingsClassFromData(data)
	const DesktopContainerRef = useRef()
	const MobileContainerRef = useRef()
	const [container, setContainer] = useState(undefined)
	const isMobile = useIsMobile()
	const branch = useCurrentBranchData()
	const { headerDescription, ...navigatorData } = data
	const [view, setView] = useState(INIT_CONTEXT.view)
	const [currentUnit, setCurrentUnit] = useState(INIT_CONTEXT.currentUnit)
	const isTextileCollectionMap = data?.isTextileCollectionMap || false
	const { units, isLoading } = useUnits(branch?.id || "", isTextileCollectionMap)

	const context = useMemo(
		() => ({
			currentUnit,
			setCurrentUnit,
			view,
			setView,
			units,
			isLoading,
			container,
			isTextileCollection: isTextileCollectionMap,
		}),
		[view, currentUnit, units, isLoading, isTextileCollectionMap, container],
	)

	useEffect(() => {
		const cont = isMobile ? MobileContainerRef.current : DesktopContainerRef.current
		setContainer(cont)
	}, [isMobile])

	useEffect(() => {
		setCurrentUnit({})
	}, [branch])

	return (
		<Wrapper className={`${paddings} ${headerDescription?.header ? "indent-desktop-4" : ""}`}>
			{headerDescription?.header && <HeaderDescription data={headerDescription} className="col-mobile-8 col-desktop-8" />}
			<NavigatorContext.Provider value={context}>
				{isMobile ? (
					<MobileNavigator ref={MobileContainerRef} data={navigatorData} />
				) : (
					<DesktopNavigator ref={DesktopContainerRef} data={navigatorData} />
				)}
			</NavigatorContext.Provider>
		</Wrapper>
	)
}

export default BranchNavigator

export function useNavigatorContext(selector) {
	return useCustomContext(NavigatorContext, selector)
}
